import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import type { AccountEntity } from '@actual-app/core/types/models';

import { convertToBaseCurrency } from '#accounts/currency';
import type { RateIndex } from '#accounts/currency';
import { useFormat } from '#hooks/useFormat';
import { useSheetValue } from '#hooks/useSheetValue';
import * as bindings from '#spreadsheet/bindings';

// Renders nothing -- subscribes to a single account's own balance cell
// (single account, so always a single currency, no conversion needed here)
// and reports it up to useGroupBalanceValue. One of these per account in
// the group, since useSheetValue can't be called a variable number of times
// from a single hook (the group's account list length varies), only via a
// variable number of *mounted components*.
function AccountBalanceSubscriber({
  accountId,
  onChange,
}: {
  accountId: string;
  onChange: (accountId: string, balance: number) => void;
}) {
  const balance =
    useSheetValue<'account', 'balance'>(bindings.accountBalance(accountId)) ??
    0;
  // Reporting up in an effect (not directly during render) avoids updating
  // the parent's state while this child is rendering, which React warns
  // about ("Cannot update a component while rendering a different
  // component").
  useEffect(() => {
    onChange(accountId, balance);
  }, [accountId, balance, onChange]);
  return null;
}

// Cross-account group total (e.g. "All accounts", "On budget", "Off
// budget") for a multi-currency budget. The underlying bindings
// (allAccountBalance, onBudgetAccountBalance, etc.) do a single SQL $sum
// across every matching account regardless of currency, which silently
// adds e.g. DKK and EUR balances together once accounts can have their own
// currency. There's no way to convert per-account inside that single SQL
// query, so instead this subscribes to each account's own (single-currency)
// balance cell individually and sums the converted values in JS -- the same
// approach already used for the Net Worth report.
//
// Returns `total` (the converted sum, for display) and `subscribers` (JSX
// that must be mounted somewhere in the tree for the per-account cells to
// actually subscribe and report back).
//
// Takes a pre-built rateIndex rather than fetching/building its own --
// this is typically called multiple times per render (once per group:
// "All accounts", "On budget", "Off budget"), and each would otherwise
// redundantly re-sort the same full rates array. Building the index once
// in the caller and sharing it here avoids that.
export function useGroupBalanceValue(
  accounts: AccountEntity[],
  rateIndex: RateIndex,
): {
  total: number;
  subscribers: ReactNode;
} {
  const format = useFormat();
  const baseCurrency = format.currency.code;

  const [nativeBalances, setNativeBalances] = useState<Record<string, number>>(
    {},
  );
  const onChange = useCallback((accountId: string, balance: number) => {
    setNativeBalances(prev =>
      prev[accountId] === balance ? prev : { ...prev, [accountId]: balance },
    );
  }, []);

  // Uses today's rate (the most recent one on file) rather than a
  // historical per-date lookup like Net Worth's chart points do -- this is
  // a live "right now" total, not a historical series, so there's no
  // meaningful past date to convert as of.
  const today = monthUtils.currentDay();

  const total = useMemo(
    () =>
      accounts.reduce(
        (sum, account) =>
          sum +
          convertToBaseCurrency(
            nativeBalances[account.id] ?? 0,
            account.currency || baseCurrency,
            baseCurrency,
            today,
            rateIndex,
          ),
        0,
      ),
    [accounts, nativeBalances, baseCurrency, rateIndex, today],
  );

  const subscribers = (
    <>
      {accounts.map(account => (
        <AccountBalanceSubscriber
          key={account.id}
          accountId={account.id}
          onChange={onChange}
        />
      ))}
    </>
  );

  return { total, subscribers };
}
